/**
 * Homebase Provider
 *
 * @author    Frontend Design Systems <FrontendDesignSystems@wayfair.com>
 * @copyright 2019 Wayfair LLC - All rights reserved
 */
import React, {createContext, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  Provider as StyleFrameworkProvider,
  styleFrameworkContext,
  ActiveBrand,
} from '@homebase/style-framework';
import {
  Provider as LocaleProvider,
  LOCALES,
  localeContext,
} from '@homebase/locale';
import {ImageProvider, imageContext} from '@homebase/image';

// Re-export all of the individual contexts and the ActiveBrand + Locales values
export {
  LOCALES,
  localeContext,
  styleFrameworkContext,
  ActiveBrand,
  imageContext,
};

// Exported for public API
export const providerContext = createContext({
  locale: '',
  cdnUrl: '',
  imageKey: '',
  styleFramework: {},
});

// The numbers below map to the STORE_ID constants defined in the PHP monolith
// here: https://github.csnzoo.com/secure/php/blob/9a7805f5e7db6e1e2d1b029ea06984ff892b71b6/includes/models/store_model.php
// Exported for tests
export const storefrontThemes = {
  PARTNER_HOME: 600,
  WAYFAIR: 49,
  ALLMODERN: 81,
  // Legacy value - shouldn't be used in code
  // Prefer BIRCHLANE
  BIRCH: 422,
  BIRCHLANE: 422,
  // Legacy value - shouldn't be used in code
  // Prefer JOSSANDMAIN
  JOSS: 450,
  JOSSANDMAIN: 450,
  PERIGOLD: 457,
  WAYFAIR_CA: 446,
  WAYFAIR_DE: 368,
  WAYFAIR_UK: 321,
};

const wayfairThemes = [
  storefrontThemes.WAYFAIR,
  storefrontThemes.WAYFAIR_CA,
  storefrontThemes.WAYFAIR_DE,
  storefrontThemes.WAYFAIR_UK,
];

// Admin and Extranet don't actually have their own STORE_ID's (since they aren't actually stores)
// In the monolith they default to the WAYFAIR_ID (49), however that would break for any feature
// available on Admin and Extranet using `<ActiveBrand>` to determine that at runtime (because they would both
// use the same number). So instead, here we are making up store ids that don't already exist,
// the numbers themselves don't really matter all that much, decided to base it off of Wayfairs ID of 49
// Exported for tests
export const enterpriseThemes = {
  ADMIN: 490,
  EXTRANET: 491,
};

// These are the supported values for the `activeTheme` prop on the Provider component
// Exported for public API
export const THEME_NAMES = {
  wayfair: 'wayfair',
  allmodern: 'allmodern',
  birchlane: 'birchlane',
  jossandmain: 'jossandmain',
  perigold: 'perigold',
  admin: 'admin',
  extranet: 'extranet',
  wayfairca: 'wayfairca',
  wayfairde: 'wayfairde',
  wayfairuk: 'wayfairuk',
  partnerhome: 'partnerhome',
};

// This function derives our legacy styleframework object structure, roughly:
// {
//   application: 'stores' | 'intranet' | 'extranet',
//   brands: {
//     activeBrand: [some number - usually a store id from the PHP monolith],
//     WAYFAIR: [Wayfairs Store ID],
//     ALLMODERN: [Allmoderns Store ID],
//     ...
//   }
// }

// Determines if the provided brand represents any Wayfair store
function getIsAnyWayfair(activeBrand) {
  return wayfairThemes.includes(activeBrand);
}

// Exported for tests
export function getStyleFramework(activeTheme, legacyStyleFramework) {
  switch (activeTheme) {
    case 'wayfair': {
      const activeBrand = storefrontThemes.WAYFAIR;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    case 'partnerhome': {
      const activeBrand = storefrontThemes.PARTNER_HOME;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    case 'allmodern': {
      const activeBrand = storefrontThemes.ALLMODERN;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    case 'birchlane': {
      const activeBrand = storefrontThemes.BIRCHLANE;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    case 'jossandmain': {
      const activeBrand = storefrontThemes.JOSSANDMAIN;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    case 'perigold': {
      const activeBrand = storefrontThemes.PERIGOLD;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    // enterprise
    case 'admin': {
      const activeBrand = enterpriseThemes.ADMIN;
      return {
        application: 'intranet',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...enterpriseThemes,
        },
      };
    }
    case 'extranet': {
      const activeBrand = enterpriseThemes.EXTRANET;
      return {
        application: 'extranet',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...enterpriseThemes,
        },
      };
    }
    // International wayfair stores
    case 'wayfairca': {
      const activeBrand = storefrontThemes.WAYFAIR_CA;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    case 'wayfairde': {
      const activeBrand = storefrontThemes.WAYFAIR_DE;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    case 'wayfairuk': {
      const activeBrand = storefrontThemes.WAYFAIR_UK;
      return {
        application: 'stores',
        brands: {
          activeBrand,
          isAnyWayfair: getIsAnyWayfair(activeBrand),
          ...storefrontThemes,
        },
      };
    }
    default: {
      return legacyStyleFramework;
      // @TODO - [MAJOR]: Enable these errors when we version bump to the next major
      //       if (typeof activeTheme === 'undefined') {
      //         throw new TypeError(`No \`activeTheme\` prop provided to the HomebaseProvider.

      // Be sure to provide one of the valid theme names, typically from the THEME_NAMES exported constant`);
      //       } else {
      //         throw new Error(`Invalid \`activeTheme\` value provided to the HomebaseProvider.

      // You provided: "${activeTheme}".

      // The value must be one of:

      //   - 'wayfair'
      //   - 'allmodern'
      //   - 'birchlane'
      //   - 'jossandmain'
      //   - 'perigold'
      //   - 'admin'
      //   - 'extranet'
      //   - 'wayfairca'
      //   - 'wayfairde'
      //   - 'wayfairuk'`);
      //       }
    }
  }
}

export default function HomebaseProvider({
  locale,
  cdnUrl,
  imageKey,
  activeTheme,
  children,
  styleFramework: legacyStyleFramework,
}) {
  const styleFramework = useMemo(
    () => getStyleFramework(activeTheme, legacyStyleFramework),
    [activeTheme, legacyStyleFramework]
  );

  return (
    <providerContext.Provider
      value={{locale, styleFramework, imageKey, cdnUrl}}
    >
      <LocaleProvider locale={locale}>
        <StyleFrameworkProvider styleFramework={styleFramework}>
          <ImageProvider imageKey={imageKey} cdnUrl={cdnUrl}>
            {children}
          </ImageProvider>
        </StyleFrameworkProvider>
      </LocaleProvider>
    </providerContext.Provider>
  );
}

HomebaseProvider.propTypes = {
  // An number representing the active brand
  children: PropTypes.node.isRequired,
  //  An object of supported locales
  locale: PropTypes.string.isRequired,
  // A string representing the current active theme for the experience
  activeTheme: PropTypes.oneOf([
    'wayfair',
    'allmodern',
    'birchlane',
    'jossandmain',
    'perigold',
    'admin',
    'extranet',
    'wayfairca',
    'wayfairde',
    'wayfairuk',
    'partnerhome',
  ]).isRequired,
  // A string that is the base URL of the CDN
  cdnUrl: PropTypes.string.isRequired,
  // A string that is the salt to use for the image hash
  imageKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  // Legacy prop to enable an incremental migration to the `activeBrand` prop
  styleFramework: PropTypes.shape({
    // A string for the supported application
    application: PropTypes.oneOf(['intranet', 'extranet', 'stores']),
    // An object of supported brands
    brands: PropTypes.shape({
      activeBrand: PropTypes.number,
      ALLMODERN: PropTypes.number,
      BIRCH: PropTypes.number,
      BIRCHLANE: PropTypes.number,
      JOSS: PropTypes.number,
      JOSSANDMAIN: PropTypes.number,
      PERIGOLD: PropTypes.number,
      WAYFAIR: PropTypes.number,
      WAYFAIR_CA: PropTypes.number,
      WAYFAIR_DE: PropTypes.number,
      WAYFAIR_UK: PropTypes.number,
    }),
  }),
};

// Not exposing `getStyleFramework`, `storefrontThemes` and `enterpriseThemes` as they are implementation details
// Only exporting them for unit tests, otherwise expose the public interface
HomebaseProvider.THEME_NAMES = THEME_NAMES;
HomebaseProvider.providerContext = providerContext;
HomebaseProvider.LOCALES = LOCALES;
HomebaseProvider.localeContext = localeContext;
HomebaseProvider.styleFrameworkContext = styleFrameworkContext;
HomebaseProvider.ActiveBrand = ActiveBrand;
HomebaseProvider.imageContext = imageContext;
